import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { handleError, handleSuccess } from '../../utils';
import { ToastContainer } from 'react-toastify';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Stack,
  CircularProgress,
  Alert,
  LinearProgress,
  Chip,
  Autocomplete,
  Paper
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  CloudUpload as UploadIcon,
  PlayCircleOutline as PlayIcon,
  Delete as DeleteIcon,
  Language as LanguageIcon,
  Assignment as TemplateIcon,
  Edit as EditIcon
} from '@mui/icons-material';

const ProgramEdit = () => {
  const [formData, setFormData] = useState({
    language: '',
    programDetail: '',
    name: '',
    url: '',
    language_temp: '',
    tempName: '',
    ageRange: [],
    templateId: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Language states
  const [languages, setLanguages] = useState([]);
  const [languagesLoading, setLanguagesLoading] = useState(true);
  
  // Program template states
  const [programTemplates, setProgramTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingProgramName, setEditingProgramName] = useState(false);
  const [editedProgramName, setEditedProgramName] = useState('');
  
  // Store original program data to match template later
  const [originalProgramData, setOriginalProgramData] = useState(null);
  
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState('');
  
  const navigate = useNavigate();
  const { id } = useParams();

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

  const axiosConfig = {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    }
  };

  // Common TextField styling
  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 3,
      '& fieldset': {
        borderRadius: 3,
      },
      '&:hover fieldset': {
        borderColor: '#673AB7',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#673AB7',
      },
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#673AB7',
    }
  };

  // Fetch languages from API
  const fetchLanguages = async () => {
    try {
      setLanguagesLoading(true);
      const response = await axios.get(`${API_URL}/api/languages?limit=100`, axiosConfig);
      const result = response.data;

      if (result.success) {
        setLanguages(result.data);
      } else {
        handleError('Failed to fetch languages');
        setLanguages([]);
      }
    } catch (error) {
      console.error('Error fetching languages:', error);
      handleError('Failed to fetch languages');
      setLanguages([]);
    } finally {
      setLanguagesLoading(false);
    }
  };

  // Fetch program templates from API
  const fetchProgramTemplates = async () => {
    try {
      setTemplatesLoading(true);
      const response = await axios.get(`${API_URL}/api/program-templates?limit=100`, axiosConfig);
      const result = response.data;
      if (result.success) {
        setProgramTemplates(result.data);
        return result.data; // Return the templates for immediate use
      } else {
        handleError('Failed to fetch program templates');
        setProgramTemplates([]);
        return [];
      }
    } catch (error) {
      console.error('Error fetching program templates:', error);
      handleError('Failed to fetch program templates');
      setProgramTemplates([]);
      return [];
    } finally {
      setTemplatesLoading(false);
    }
  };

  // Function to find matching template
  const findMatchingTemplate = (programData, templates) => {
    if (!templates || templates.length === 0) return null;
    
    let matchingTemplate = null;
    
    // Method 1: Try to match by name (exact match)
    if (programData.name) {
      matchingTemplate = templates.find(template => 
        template.name.toLowerCase() === programData.name.toLowerCase()
      );
    }
    
    // Method 2: If no name match, try to find by age range
    if (!matchingTemplate && programData.ageRange && programData.ageRange.length > 0) {
      matchingTemplate = templates.find(template => {
        if (!template.ageRange || template.ageRange.length === 0) return false;
        
        const programAges = programData.ageRange.map(a => a.age).sort((a, b) => a - b);
        const templateAges = template.ageRange.map(a => a.age).sort((a, b) => a - b);
        
        // Check if age arrays are identical
        if (programAges.length !== templateAges.length) return false;
        return programAges.every((age, index) => age === templateAges[index]);
      });
    }
    
    // Method 3: If still no match, try partial name matching
    if (!matchingTemplate && programData.name) {
      matchingTemplate = templates.find(template => 
        template.name.toLowerCase().includes(programData.name.toLowerCase()) ||
        programData.name.toLowerCase().includes(template.name.toLowerCase())
      );
    }
    
    return matchingTemplate;
  };

  // Fetch existing program data
  const fetchProgram = async () => {
    try {
      const url = `${API_URL}/api/programs/${id}`;
      const response = await axios.get(url, axiosConfig);
      const result = response.data;

      if (result.success) {
        const program = result.data;
        
        setOriginalProgramData(program); // Store for later template matching
        
        setFormData({
          language: program.language || '',
          programDetail: program.programDetail || '',
          name: program.name || '',
          url: program.url || '',
          language_temp: program.language_temp || '',
          tempName: program.tempName || '',
          ageRange: program.ageRange || [],
          templateId: program.templateId || ''
        });

        setEditedProgramName(program.name || '');

        // Set video preview if URL exists
        if (program.url) {
          setVideoPreview(program.url);
        }

        return program;
      } else {
        handleError('Program not found');
        navigate('/programs');
        return null;
      }
    } catch (error) {
      console.error('Error fetching program:', error);
      handleError('Failed to fetch program details');
      navigate('/programs');
      return null;
    }
  };

  // Initialize data and match template
  useEffect(() => {
    const initializeData = async () => {
      try {
        setInitialLoading(true);
        
        // Fetch all data in parallel
        const [templates, programData] = await Promise.all([
          fetchProgramTemplates(),
          fetchProgram(),
          fetchLanguages()
        ]);
        
        // After both templates and program data are loaded, find matching template
        if (templates && templates.length > 0 && programData) {
          const matchingTemplate = findMatchingTemplate(programData, templates);
          
          if (matchingTemplate) {
            console.log('Found matching template:', matchingTemplate);
            setSelectedTemplate(matchingTemplate);
            // Update form data with template information
            setFormData(prev => ({
              ...prev,
              templateId: matchingTemplate._id,
              ageRange: matchingTemplate.ageRange || prev.ageRange
            }));
          } else {
            console.log('No matching template found for program:', programData.name);
          }
        }
      } catch (error) {
        console.error('Error initializing data:', error);
      } finally {
        setInitialLoading(false);
      }
    };

    if (id) {
      initializeData();
    }
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle language selection
  const handleLanguageChange = (event, newValue) => {
    setFormData(prev => ({
      ...prev,
      language: newValue ? newValue.name : ''
    }));
  };

  // Handle program template selection
  const handleTemplateChange = (event, newValue) => {
    if (newValue) {
      setSelectedTemplate(newValue);
      setFormData(prev => ({
        ...prev,
        templateId: newValue._id,
        ageRange: newValue.ageRange
      }));
      // Don't change the program name automatically on edit, keep existing name
      setEditedProgramName(formData.name);
      setEditingProgramName(false);
    } else {
      setSelectedTemplate(null);
      setFormData(prev => ({
        ...prev,
        templateId: '',
        ageRange: []
      }));
      setEditedProgramName('');
      setEditingProgramName(false);
    }
  };

  // Handle program name editing
  const handleEditProgramName = () => {
    setEditingProgramName(true);
  };

  const handleSaveProgramName = () => {
    setFormData(prev => ({
      ...prev,
      name: editedProgramName
    }));
    setEditingProgramName(false);
  };

  const handleCancelProgramName = () => {
    setEditedProgramName(formData.name);
    setEditingProgramName(false);
  };

  const formatAgeRange = (ageRange) => {
    if (!ageRange || ageRange.length === 0) return null;
    
    const ages = ageRange.map(age => age.age).sort((a, b) => a - b);
    
    if (ages.length === 1) return `${ages[0]} years`;
    if (ages.length === 2) return `${ages[0]}-${ages[1]} years`;
    return `${ages[0]}-${ages[ages.length - 1]} years`;
  };

  const handleVideoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      handleError('Please select a valid video file');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      handleError('Video file size should be less than 50MB');
      return;
    }

    setVideoFile(file);
    setVideoPreview(URL.createObjectURL(file));

    try {
      setUploading(true);
      setUploadProgress(0);

      const uploadFormData = new FormData();
      uploadFormData.append('video', file);

      const response = await axios.post(`${API_URL}/mediaqwerty/upload`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      if (response.data.success) {
        const videoUrl = `${API_URL}${response.data.url}`;
        setFormData(prev => ({
          ...prev,
          url: videoUrl
        }));
        handleSuccess('Video uploaded successfully');
      } else {
        handleError('Failed to upload video');
      }
    } catch (error) {
      console.error('Upload error:', error);
      if (error.response) {
        const errorMessage = error.response.data?.message || 'Failed to upload video';
        handleError(errorMessage);
      } else if (error.request) {
        handleError('Network error - please check your connection');
      } else {
        handleError('Failed to upload video');
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const removeVideo = () => {
    setVideoFile(null);
    setVideoPreview('');
    setFormData(prev => ({
      ...prev,
      url: ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.language || !formData.programDetail || !formData.name) {
      handleError('Please fill in language, program details, and program name');
      return;
    }

    try {
      setLoading(true);

      const submitData = {
        ...formData,
        ageRange: formData.ageRange
      };

      const url = `${API_URL}/api/programs/${id}`;
      const response = await axios.put(url, submitData, axiosConfig);
      const result = response.data;

      if (result.success) {
        handleSuccess('Program updated successfully');
        setTimeout(() => {
          navigate('/programs');
        }, 1000);
      } else {
        handleError(result.message || 'Failed to update program');
      }
    } catch (error) {
      console.error('Error updating program:', error);
      if (error.response) {
        const errorMessage = error.response.data?.message || 'Failed to update program';
        handleError(errorMessage);
      } else {
        handleError('Network error - please check your connection');
      }
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Box 
        sx={{ 
          marginLeft: '320px',
          marginRight: '24px',
          marginTop: '24px',
          padding: '20px',
          minHeight: '100vh',
          backgroundColor: 'white', // White background
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2
          }}
        >
          <CircularProgress size={60} sx={{ color: '#673AB7' }} />
          <Typography variant="h6" color="text.secondary">
            Loading program details...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        marginLeft: '320px',
        marginRight: '24px',
        marginTop: '24px',
        marginBottom: '24px',
        padding: '20px',
        minHeight: '100vh',
        backgroundColor: 'white' // Changed to white background
      }}
    >
      <Container maxWidth="md" sx={{ p: 0 }}>
        {/* Title */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              color: '#673AB7',
              mb: 1,
            }}
          >
            Edit Program
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={4}>
            {/* Program Template Selection */}
            <Box>
              <Typography variant="h6" gutterBottom>Programs</Typography>
              {templatesLoading ? (
                <CircularProgress size={20} />
              ) : (
                <Autocomplete
                  options={programTemplates}
                  getOptionLabel={(option) => option.name || ''}
                  value={selectedTemplate}
                  onChange={handleTemplateChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Programs"
                      variant="outlined"
                      sx={textFieldStyles}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <TemplateIcon sx={{ color: '#673AB7', mr: 1 }} />
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box>
                        <Typography variant="subtitle1">{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Ages: {formatAgeRange(option.ageRange)}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  sx={{
                    '& .MuiAutocomplete-popupIndicator': {
                      color: '#673AB7'
                    }
                  }}
                />
              )}
              {!templatesLoading && programTemplates.length === 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  No programs available. Please create Programs first in the Programs section.
                </Alert>
              )}
            </Box>

            {/* Selected Template Info with Editable Program Name */}
            {selectedTemplate && (
              <Paper 
                elevation={1} 
                sx={{ 
                  p: 3, 
                  borderRadius: 2, 
                  backgroundColor: '#f8f9ff', // Light background instead of primary.light
                  border: '1px solid #673AB7'
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ color: '#673AB7' }}>
                  Selected Programs Information
                </Typography>
                
                {/* Program Name with Edit Option */}
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom sx={{ color: '#673AB7' }}>
                    Program Name:
                  </Typography>
                  {editingProgramName ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        value={editedProgramName}
                        onChange={(e) => setEditedProgramName(e.target.value)}
                        size="small"
                        sx={textFieldStyles}
                      />
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleSaveProgramName}
                        sx={{ backgroundColor: 'success.main' }}
                      >
                        Save
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleCancelProgramName}
                        sx={{ borderColor: '#673AB7', color: '#673AB7' }}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body1" sx={{ color: '#333' }}>
                        {formData.name}
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={handleEditProgramName}
                        sx={{ color: '#673AB7', borderColor: '#673AB7' }}
                        variant="outlined"
                      >
                        Edit Name
                      </Button>
                    </Stack>
                  )}
                </Box>

                {/* Age Range */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom sx={{ color: '#673AB7' }}>
                    Target Age:
                  </Typography>
                  <Chip
                    label={formatAgeRange(selectedTemplate.ageRange)}
                    sx={{
                      backgroundColor: '#673AB7',
                      color: 'white',
                      fontWeight: 600
                    }}
                  />
                </Box>
              </Paper>
            )}

            {/* Language Dropdown */}
            <Box>
              <Typography variant="h6" gutterBottom>Language</Typography>
              {languagesLoading ? (
                <Typography variant="body2">Loading languages...</Typography>
              ) : (
                <Autocomplete
                  options={languages}
                  getOptionLabel={(option) => option.name || ''}
                  value={languages.find(lang => lang.name === formData.language) || null}
                  onChange={handleLanguageChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Language"
                      variant="outlined"
                      required
                      sx={textFieldStyles}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <LanguageIcon sx={{ color: '#673AB7', mr: 1 }} />
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <LanguageIcon sx={{ mr: 1 }} />
                      {option.name}
                    </Box>
                  )}
                  sx={{
                    '& .MuiAutocomplete-popupIndicator': {
                      color: '#673AB7'
                    }
                  }}
                />
              )}
              {!languagesLoading && languages.length === 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  No languages available. Please add languages first in the Languages section.
                </Alert>
              )}
            </Box>

            {/* Program Details */}
            <TextField
              fullWidth
              label="Program Details"
              name="programDetail"
              value={formData.programDetail}
              onChange={handleChange}
              required
              multiline
              rows={4}
              variant="outlined"
              placeholder="Describe the program objectives, activities, and learning outcomes..."
              sx={textFieldStyles}
            />

            {/* Language Template */}
            <TextField
              fullWidth
              label="Language Template (Optional)"
              name="language_temp"
              value={formData.language_temp}
              onChange={handleChange}
              variant="outlined"
              placeholder="e.g., pt_BR, es, en"
              sx={textFieldStyles}
            />

            {/* Template Name */}
            <TextField
              fullWidth
              label="Template Name (Optional)"
              name="tempName"
              value={formData.tempName}
              onChange={handleChange}
              variant="outlined"
              placeholder="e.g., bricks_challenge_pt, galileo_technic_es"
              sx={textFieldStyles}
            />

            {/* Video Upload Section */}
            <Box>
              <Typography variant="h6" gutterBottom>Video Upload (Optional)</Typography>
              
              {!formData.url && (
                <Box
                  sx={{
                    border: '2px dashed #673AB7',
                    borderRadius: 3,
                    p: 4,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    backgroundColor: 'white',
                    '&:hover': {
                      borderColor: '#5e35b1',
                      backgroundColor: '#f5f5f5',
                      transform: 'translateY(-1px)'
                    }
                  }}
                  onClick={() => document.getElementById('video-upload').click()}
                >
                  <UploadIcon sx={{ fontSize: 48, color: '#673AB7', mb: 1 }} />
                  <Typography variant="body1" sx={{ color: '#673AB7', mb: 1, fontWeight: 600 }}>
                    Click to upload video or drag and drop
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Supported formats: MP4, AVI, MOV (Max 50MB)
                  </Typography>
                  <input
                    id="video-upload"
                    type="file"
                    accept="video/*"
                    style={{ display: 'none' }}
                    onChange={handleVideoUpload}
                  />
                </Box>
              )}

              {uploading && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Uploading video... {uploadProgress}%
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={uploadProgress} 
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#673AB7',
                        borderRadius: 4
                      }
                    }}
                  />
                </Box>
              )}

              {formData.url && (
                <Box sx={{ mt: 2 }}>
                  {/* Simple Success Message with Remove Button */}
                  <Box 
                    display="flex" 
                    justifyContent="space-between" 
                    alignItems="center" 
                    sx={{ 
                      mb: 2, 
                      p: 2, 
                      backgroundColor: '#e8f5e8', 
                      borderRadius: 2,
                      border: '1px solid #4caf50'
                    }}
                  >
                    <Typography variant="body1" color="success.dark" fontWeight={600}>
                      ✅ Video uploaded successfully!
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={removeVideo}
                      sx={{ 
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600
                      }}
                    >
                      Remove
                    </Button>
                  </Box>

                  {/* Full Width Video Player */}
                  {videoPreview && (
                    <Box sx={{ 
                      width: '100%', 
                      borderRadius: 2, 
                      overflow: 'hidden',
                      backgroundColor: '#000',
                      border: '1px solid #ddd'
                    }}>
                      <video
                        src={videoPreview}
                        controls
                        preload="metadata"
                        style={{
                          width: '100%',
                          height: 'auto',
                          minHeight: '250px',
                          maxHeight: '400px',
                          display: 'block',
                          objectFit: 'contain'
                        }}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'center',
                mt: 4
              }}
            >
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/programs')}
                sx={{
                  borderColor: '#673AB7',
                  color: '#673AB7',
                  borderRadius: 3,
                  px: 4,
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#5e35b1',
                    backgroundColor: 'rgba(103, 58, 183, 0.04)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading || uploading || !formData.language}
                sx={{
                  backgroundColor: '#673AB7',
                  borderRadius: 3,
                  px: 4,
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(103, 58, 183, 0.3)',
                  '&:hover': {
                    backgroundColor: '#5e35b1',
                    boxShadow: '0 6px 16px rgba(103, 58, 183, 0.4)'
                  }
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    Updating...
                  </>
                ) : (
                  <>
                    <SaveIcon sx={{ mr: 1 }} />
                    Update Program
                  </>
                )}
              </Button>
            </Box>
          </Stack>
        </Box>

        <ToastContainer />
      </Container>
    </Box>
  );
};

export default ProgramEdit;
